import React, { useState } from 'react';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import Autocomplete from '../../components/SimpleAutocomplete';
import { useListJobConfigsQuery } from 'react-scale/corp/lib/api/growth';
import { ScreenType, ScreenFilter } from '@scale/shared-types';
import { Button, Typography } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';

const ResumeScreeningConfigAutocomplete: React.FC<{
  selectedResumeScreeningConfig: { id: string; title: string; nodeOnly?: boolean };
  setSelectedResumeScreeningConfig: (value: {
    id: string;
    title: string;
    nodeOnly?: boolean;
  }) => void;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  showViewButton?: boolean;
  showCreateButton?: boolean;
}> = ({
  selectedResumeScreeningConfig,
  setSelectedResumeScreeningConfig,
  disabled,
  className,
  buttonClassName,
  showViewButton = false,
  showCreateButton = false,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebouncedValue(inputValue, 200);

  const { data: allJobConfigs, isLoading: isListJobConfigsFetching } = useListJobConfigsQuery(
    ScreenFilter.Growth,
  );
  const options =
    allJobConfigs
      ?.filter(
        jobConfig =>
          jobConfig.name.toLowerCase().includes(debouncedInputValue.toLowerCase()) &&
          jobConfig.type === ScreenType.OCP,
      )
      .map(jobConfig => ({
        id: jobConfig._id,
        title: jobConfig.name,
      })) ?? [];

  return (
    <div className={twMerge('flex items-center space-x-2', className)}>
      <Autocomplete
        className="w-full z-[100]"
        options={options}
        value={{
          ...selectedResumeScreeningConfig,
          title: selectedResumeScreeningConfig.title.length
            ? selectedResumeScreeningConfig.title
            : options.find(option => option.id === selectedResumeScreeningConfig.id)?.title ?? '',
        }}
        setSelectedOption={setSelectedResumeScreeningConfig}
        onInputChange={setInputValue}
        disabled={disabled}
        loading={isListJobConfigsFetching}
        placeholder="Search resume screening config by name..."
      />
      {showViewButton && selectedResumeScreeningConfig.id && (
        <Button
          variant="secondary"
          color="neutral"
          className={twMerge('h-5/6', buttonClassName)}
          onClick={() =>
            window.open(
              `/corp/growth-center/resume-screening/${selectedResumeScreeningConfig.id}`,
              '_blank',
            )
          }
        >
          View
        </Button>
      )}
      {showCreateButton && !selectedResumeScreeningConfig?.id && (
        <div className="flex items-center space-x-2">
          <Typography.Paragraph3>or</Typography.Paragraph3>
          <Button
            variant="secondary"
            color="neutral"
            className={twMerge('h-5/6 w-max', buttonClassName)}
            onClick={() => window.open('/corp/growth-center/resume-screening', '_blank')}
          >
            Create a Screening
          </Button>
        </div>
      )}
      {(!showCreateButton || selectedResumeScreeningConfig?.id) && (
        <Button
          variant="secondary"
          className={twMerge('h-5/6', buttonClassName)}
          onClick={() => {
            setSelectedResumeScreeningConfig({ id: '', title: '', nodeOnly: false });
            setInputValue('');
          }}
          disabled={!selectedResumeScreeningConfig.id || disabled}
        >
          Reset
        </Button>
      )}
    </div>
  );
};

export default ResumeScreeningConfigAutocomplete;
